import * as React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import MasterAddEditAssetData from '../MasterAddEditAssetData';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { getAssetById, GetSimulateAssetfields, getMyAssetsPaginated } from '../../../REST/assets';
import { FieldConfigGrid } from '../../../Utils/fieldconfigs';
import moment from 'moment';
import PageShell from '../../../themes/components/PageShell';
import PageHeader from '../../../themes/components/PageHeader';
import PageContent from '../../../themes/components/PageContent';
import Action from '../../../themes/components/Action';
import ResponsiveDataGrid from '../../../themes/components/ResponsiveDataGrid';

export default function AssetDataList() {
    const navigate = useNavigate();
    const location = useLocation();
    const [add, setAdd] = useState(null);
    const [edit, setEdit] = useState(null);
    const [refresh, setRefresh] = useState(false);
    const [assetType, setAssetType] = useState(null);
    const [config, setConfig] = useState(null);
    const id = location.pathname.replace('/asset/assetList/', '');

    const handleEdit = (params) => {
        setEdit(params.row._id);
    };

    const columns = useMemo(() => {
        let cols = [
            {
                field: '_id',
                headerName: 'Action',
                width: 80,
                sortable: false,
                filterable: false,
                minVisible: 'xs',
                renderCell: (params) => (
                    <Tooltip title="Edit asset">
                        <IconButton
                            size="small"
                            onClick={(evt) => {
                                evt.preventDefault();
                                evt.stopPropagation();
                                handleEdit(params);
                            }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )
            }
        ];

        if (config != null) {
            cols = cols.concat(FieldConfigGrid(config).map((col) => {
                // Show only Asset # and Last Inspected Date on mobile
                const mobileFields = ['Asset #', 'Last Inspected Date'];
                const isMobileVisible = mobileFields.includes(col.headerName);
                return {
                    ...col,
                    // Use flex instead of width for mobile columns to fit screen
                    width: isMobileVisible ? undefined : col.width,
                    flex: isMobileVisible ? 1 : undefined,
                    minVisible: isMobileVisible ? 'xs' : 'md'
                };
            }));
        }

        cols.push({
            field: 'status',
            headerName: 'Asset Status',
            flex: 1,
            minVisible: 'md',
            type: 'singleSelect',
            valueOptions: ['Created', 'In Progress', 'Completed']
        });
        cols.push({
            field: 'createdAt',
            headerName: 'Created On',
            flex: 1,
            minVisible: 'lg',
            valueFormatter: (params) => moment(params.value).format('MM/DD/YYYY HH:mm')
        });

        return cols;
    }, [config]);

    const fetchAssets = useCallback(async (page, limit, filters, sort) => {
        const params = { page, limit };

        if (Object.keys(filters).length > 0) {
            params.filters = JSON.stringify(filters);
        }

        if (sort) {
            params.sort = JSON.stringify(sort);
        }

        const result = await getMyAssetsPaginated(id, params);

        if (result.data?.data) {
            return {
                data: result.data.data,
                pagination: result.data.pagination
            };
        }

        return { data: [], pagination: { total: 0, page: 1, pages: 1 } };
    }, [id, refresh]);

    useEffect(() => {
        Promise.all([GetSimulateAssetfields(), getAssetById(id)])
            .then((values) => {
                setConfig(values[0].data);
                setAssetType(values[1].data);
            })
            .catch(() => {
                navigate('/asset');
            });
    }, [id, navigate]);

    const handleCloseAdd = (ret) => {
        setAdd(null);
        if (ret) setRefresh((prev) => !prev);
    };

    const handleCloseEdit = (ret) => {
        setEdit(null);
        if (ret) setRefresh((prev) => !prev);
    };

    if (!config || !assetType) {
        return null;
    }

    return (
        <PageShell>
            <PageHeader
                title={assetType?.name ? `${assetType.name} Assets` : 'Assets'}
                back
                onBack={() => navigate('/asset')}
                actions={
                    <Action
                        primary
                        icon={<AddIcon />}
                        label={assetType?.name ? `Add ${assetType.name} Asset` : 'Add Asset'}
                        onClick={() => setAdd(id)}
                    />
                }
            />

            <PageContent fill>
                <ResponsiveDataGrid
                    columns={columns}
                    fetchData={fetchAssets}
                    initialPageSize={10}
                    pageSizeOptions={[10, 25, 50, 100]}
                    getRowId={(row) => row._id}
                />
            </PageContent>

            {add !== null && <MasterAddEditAssetData assetTypes={[assetType]} assetId={id} handleClose={handleCloseAdd} />}
            {edit !== null && <MasterAddEditAssetData id={edit} assetTypes={[assetType]} assetId={id} handleClose={handleCloseEdit} />}
        </PageShell>
    );
}
